/**
 * Every record type a conversation may be about.
 *
 * Ordered as the filter should read — the things people talk about most first,
 * not alphabetically, because this list is rendered as a filter's options.
 */
export const THREAD_ENTITY_TYPES = [
    { key: 'Event', label: 'Event' },
    { key: 'ExpenseItem', label: 'Expense' },
    { key: 'Vendor', label: 'Vendor' },
    { key: 'Venue', label: 'Venue' },
    { key: 'User', label: 'Person' },
    { key: 'Guest', label: 'Guest' },
    { key: 'Inquiry', label: 'Inquiry' },
    { key: 'PlanningItem', label: 'Item' },
    { key: 'Questionnaire', label: 'Questionnaire' },
    { key: 'Document', label: 'Document' },
    { key: 'Checklist', label: 'Checklist' },
    { key: 'Timeline', label: 'Timeline' },
];
/** What a thread with no record at all is called. */
export const GENERAL_THREAD_LABEL = 'General Inquiry';
const BY_KEY = new Map(THREAD_ENTITY_TYPES.map((entity) => [entity.key, entity]));
/**
 * The label for a record type.
 *
 * An unknown type returns the key itself rather than a blank or a throw: a
 * thread written by a route this registry has not caught up with should still
 * render something a person can read, and `'EntityLink'` is a worse label than
 * `'Vendor'` but a far better one than nothing.
 */
export function threadEntityLabel(entityType) {
    if (!entityType)
        return GENERAL_THREAD_LABEL;
    return BY_KEY.get(entityType)?.label ?? entityType;
}
/**
 * The topic line: `"Event: Smith Wedding"`.
 *
 * ⚠️ **A record with no name falls back to its id, never to the bare label.**
 * "Event" alone reads as though the thread is about events in general, and two
 * such rows are indistinguishable. The id is ugly and unambiguous, which is the
 * right trade for a line whose job is to tell rows apart.
 */
export function threadTopic(entityType, entityId, entityName) {
    if (!entityType)
        return GENERAL_THREAD_LABEL;
    const label = threadEntityLabel(entityType);
    const name = entityName?.trim();
    return `${label}: ${name && name !== '' ? name : (entityId ?? '')}`.trim();
}
/**
 * The Link chip's label — shorter than the topic's for a thread about nothing.
 *
 * The topic already says "General Inquiry"; a chip beside it repeating the
 * same two words is the row saying one thing twice. "General" is what the
 * reference implementation uses, and it reads as a *category* rather than as a
 * restatement.
 */
export function threadLinkLabel(entityType) {
    return entityType ? threadEntityLabel(entityType) : 'General';
}
/** Whether a conversation may be started on this record type. */
export function isThreadEntityType(entityType) {
    return BY_KEY.has(entityType);
}
//# sourceMappingURL=thread-entity.js.map